import { useEffect, useState } from 'react'
import { Check, Copy, KeyRound, TriangleAlert } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field, Input, Select } from '@/components/ui/input'
import { ErrorNote } from '@/components/ui/misc'
import { Dialog } from '@/components/ui/overlay'
import {
  ALL_PERMISSIONS,
  api,
  ApiError,
  PERMISSION_HELP,
  type CreatedApiKey,
  type Permission,
} from '@/lib/api'
import { cn } from '@/lib/cn'

const PRESETS: { label: string; permissions: Permission[] }[] = [
  { label: 'Read only', permissions: ['read', 'download'] },
  { label: 'Read + upload', permissions: ['read', 'download', 'upload'] },
  {
    label: 'Full file access',
    permissions: ['read', 'download', 'upload', 'delete', 'delete-directory', 'rename'],
  },
]

const EXPIRY_OPTIONS = [
  { value: '', label: 'Never expires' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
]

export function CreateKeyDialog({
  availableBuckets,
  onClose,
  onCreated,
}: {
  availableBuckets: string[]
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [permissions, setPermissions] = useState<Permission[]>(['read', 'download'])
  const [buckets, setBuckets] = useState<string[]>([])
  const [expiry, setExpiry] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<CreatedApiKey | null>(null)
  const [copied, setCopied] = useState(false)

  // With a single bucket there is no choice to make, so make it for them.
  useEffect(() => {
    if (availableBuckets.length === 1) setBuckets([availableBuckets[0] as string])
  }, [availableBuckets])

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const result = await api.createApiKey({
        name: name.trim(),
        permissions,
        buckets,
        expiresInDays: expiry ? Number(expiry) : null,
      })
      setCreated(result)
      onCreated()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the key')
    } finally {
      setBusy(false)
    }
  }

  async function copy() {
    if (!created) return
    try {
      await navigator.clipboard.writeText(created.key)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard blocked; the field is selectable as a fallback.
    }
  }

  // --- the key, shown exactly once -----------------------------------------

  if (created) {
    return (
      <Dialog
        open
        size="lg"
        onClose={onClose}
        title={`Key created for "${created.name}"`}
        footer={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div>
              <p className="font-medium">Copy this key now</p>
              <p className="text-muted-foreground">
                Only its hash is stored, so it cannot be shown again. If it is lost, revoke this
                key and issue a new one.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-2">
            <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              readOnly
              value={created.key}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 bg-transparent font-mono text-xs outline-none"
              aria-label="API key"
            />
            <button
              onClick={() => void copy()}
              className="shrink-0 rounded p-1 hover:bg-muted"
              aria-label="Copy key"
            >
              {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>

          <div className="space-y-1 rounded-md border border-border bg-surface p-3 text-xs">
            <p className="font-medium">The consuming app sends it as either header:</p>
            <p className="font-mono text-muted-foreground">Authorization: Bearer {created.key.slice(0, 18)}…</p>
            <p className="font-mono text-muted-foreground">X-API-Key: {created.key.slice(0, 18)}…</p>
          </div>

          <dl className="grid grid-cols-3 gap-2 text-sm">
            <dt className="text-muted-foreground">Buckets</dt>
            <dd className="col-span-2">{created.buckets.join(', ')}</dd>
            <dt className="text-muted-foreground">Permissions</dt>
            <dd className="col-span-2">{created.permissions.join(', ')}</dd>
            <dt className="text-muted-foreground">Expires</dt>
            <dd className="col-span-2">{created.expiresAt ?? 'Never'}</dd>
          </dl>
        </div>
      </Dialog>
    )
  }

  // --- the form -------------------------------------------------------------

  const canSubmit = name.trim().length > 0 && permissions.length > 0 && buckets.length > 0

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title="New API key"
      description="A credential for one application. Grant only what that app actually needs."
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={busy} disabled={!canSubmit}>
            Create key
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && <ErrorNote>{error}</ErrorNote>}

        <Field
          label="Application name"
          htmlFor="key-name"
          hint="Appears in the audit log against everything this key does."
        >
          <Input
            id="key-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="invoice-app"
            autoFocus
          />
        </Field>

        {/* Buckets */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Buckets</p>
          <p className="text-xs text-muted-foreground">
            This key cannot see or touch any bucket outside this list — it will not even appear
            in its bucket listing.
          </p>
          <div className="flex flex-wrap gap-2">
            {availableBuckets.map((bucket) => (
              <button
                key={bucket}
                type="button"
                onClick={() => setBuckets((b) => toggle(b, bucket))}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm transition-colors',
                  buckets.includes(bucket)
                    ? 'border-accent bg-accent/12 font-medium text-accent'
                    : 'border-border hover:bg-muted',
                )}
              >
                {bucket}
              </button>
            ))}
          </div>
        </div>

        {/* Permissions */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">Permissions</p>
            <div className="flex gap-1">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  size="sm"
                  variant="ghost"
                  onClick={() => setPermissions(preset.permissions)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          <ul className="divide-y divide-border rounded-md border border-border">
            {ALL_PERMISSIONS.map((permission) => (
              <li key={permission}>
                <label className="flex cursor-pointer items-start gap-2.5 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={permissions.includes(permission)}
                    onChange={() => setPermissions((p) => toggle(p, permission))}
                    className="mt-0.5 h-4 w-4 accent-[var(--accent)]"
                  />
                  <span>
                    <span className="block font-mono text-sm">{permission}</span>
                    <span className="block text-xs text-muted-foreground">
                      {PERMISSION_HELP[permission]}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>

          {permissions.includes('admin') && (
            <p className="text-xs text-warning">
              <strong>admin</strong> lets this key create and revoke other keys, including ones
              with more access than itself. Grant it only to something that manages credentials.
            </p>
          )}
          {permissions.includes('delete') && (
            <p className="text-xs text-muted-foreground">
              Deletion is still refused by HCP for files under retention or legal hold.
            </p>
          )}
        </div>

        <Field label="Expiry" htmlFor="key-expiry">
          <Select id="key-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)}>
            {EXPIRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  )
}
